import { useCallback, useRef, useState, type DragEvent } from 'react';
import { Spinner, makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { ArrowUpload24Regular } from '@fluentui/react-icons';
import { acceptAttr, esAceptado, motivoDeRechazo } from '@supernova/shared';
import { DWG_DXF_ENABLED } from '../lib/flags.js';

const useStyles = makeStyles({
  zone: {
    ...shorthands.border('2px', 'dashed', 'var(--border-default)'),
    borderRadius: 'var(--radius-xl)',
    padding: 'var(--space-xxl)',
    background: 'var(--bg-surface)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-m)',
    cursor: 'pointer',
    transition: 'all var(--duration-fast) var(--easing-curve)',
    textAlign: 'center',
  },
  hover: {
    ...shorthands.borderColor('var(--opus-brand-60)'),
    background: 'rgba(12, 111, 214, 0.06)',
  },
  busy: { cursor: 'wait', opacity: 0.7 },
  icon: {
    width: '48px',
    height: '48px',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-raised)',
    display: 'grid',
    placeItems: 'center',
    color: 'var(--opus-brand-70)',
  },
  primary: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--fs-400)',
    fontWeight: 600,
    margin: 0,
  },
  hint: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-200)',
    color: 'var(--text-tertiary)',
    margin: 0,
  },
  progressWrap: {
    width: '100%',
    height: '6px',
    background: 'var(--bg-raised)',
    borderRadius: 'var(--radius-circular)',
    overflow: 'hidden',
    marginTop: 'var(--space-m)',
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, var(--opus-brand-60), var(--opus-accent-60))',
    transition: 'width var(--duration-fast) linear',
  },
});

// Qué se acepta y qué se le dice al usuario cuando su archivo no entra: la lista
// es COMPARTIDA con el API (`@supernova/shared`), para que la pantalla y el
// servidor no puedan contradecirse. Los motivos de cada exclusión están ahí.

interface Props {
  onFiles: (files: File[]) => Promise<void>;
  busy?: boolean;
  progress?: number; // 0..1
  label?: string;
}

export function DropZone({ onFiles, busy, progress, label }: Props) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [hover, setHover] = useState(false);

  const [rejected, setRejected] = useState<string[]>([]);
  const aceptacion = { dwgDxf: DWG_DXF_ENABLED };

  const handleFiles = useCallback(
    async (list: FileList | null) => {
      if (!list || list.length === 0 || busy) return;
      // Validar extensión también en drag&drop (el `accept` del input solo
      // filtra el file-picker; arrastrando se puede soltar cualquier cosa).
      const all = Array.from(list);
      const ok = all.filter((f) => esAceptado(f.name, aceptacion));
      const bad = all.filter((f) => !ok.includes(f));
      setRejected(bad.map((f) => f.name));
      if (ok.length === 0) return;
      await onFiles(ok);
    },
    [busy, onFiles],
  );

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setHover(false);
    void handleFiles(e.dataTransfer.files);
  }

  return (
    <div
      className={mergeClasses(styles.zone, hover && styles.hover, busy && styles.busy)}
      onDragOver={(e) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      onClick={() => !busy && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      data-demo="dropzone"
    >
      <div className={styles.icon}>
        {busy ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}
      </div>
      <p className={styles.primary}>{label ?? 'Arrastra archivos aquí o haz clic para subir'}</p>
      <p className={styles.hint}>
        PDF · DOC/DOCX · XLS/XLSX · TXT/CSV{DWG_DXF_ENABLED ? ' · DWG/DXF/IFC' : ''}
      </p>
      {rejected.length > 0 && (
        <p className={styles.hint} style={{ color: 'var(--semantic-warning)' }}>
          {motivoDeRechazo(rejected, aceptacion)}
        </p>
      )}

      {busy && progress !== undefined && (
        <div className={styles.progressWrap}>
          <div className={styles.progressBar} style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={acceptAttr(aceptacion)}
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
    </div>
  );
}
